import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Modal,
  Form,
  message,
  Popconfirm,
  Tag,
  Space,
  Row,
  Col,
  Collapse,
  Tooltip,
  Typography,
  Alert,
  Transfer
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  SyncOutlined,
  DownloadOutlined,
  UploadOutlined,
  UserOutlined,
  TeamOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { 
  LopHocPhan, 
  Course,
  Subject,
  PaginationInfo
} from '../../../../shared/types/database';
import type { 
  LopHocPhanListParams, 
  LopHocPhanCreateRequest, 
  LopHocPhanUpdateRequest,
  LopHocPhanWithJoins,
  LopHocPhanSyncRequest,
  LopHocPhanListResponse
} from '../../../../shared/types/api';
import lopHocPhanService from '../../services/lopHocPhanService';
import courseService from '../../services/courseService';
import { listSubjects } from '../../services/subjectService';
import { AuthService } from '../../services/authService';
import sinhVienService from '../../services/sinhVienService';
import type { TransferDirection, TransferItem } from 'antd/es/transfer';
import { classService } from '../../services/classService';

const { Panel } = Collapse;
const { Title } = Typography;
const { Option } = Select;

interface GroupedLopHocPhan {
  course: Course;
  subjects: Array<{
    subject: Subject;
    lopHocPhans: LopHocPhanWithJoins[];
  }>;
}

interface StudentTransferItem extends TransferItem {
  key: string;
  title: string;
  description: string;
}

const LopHocPhanManagementPage: React.FC = () => {
  const [lopHocPhans, setLopHocPhans] = useState<LopHocPhanWithJoins[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [groupedData, setGroupedData] = useState<GroupedLopHocPhan[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingLopHocPhan, setEditingLopHocPhan] = useState<LopHocPhanWithJoins | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<string | undefined>();
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0
  });

  const [form] = Form.useForm();

  // Sync All modal state (following legacy pattern)
  const [syncAllModalVisible, setSyncAllModalVisible] = useState(false);
  const [syncAllLoading, setSyncAllLoading] = useState(false);
  const [syncAllForm] = Form.useForm();

  const [studentModalVisible, setStudentModalVisible] = useState(false);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [sourceStudents, setSourceStudents] = useState<StudentTransferItem[]>([]);
  const [currentLHP, setCurrentLHP] = useState<any>(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [syncStudentsLoading, setSyncStudentsLoading] = useState(false);  // New state for sync button
  
  // States for cascading select
  const [coursesForSelection, setCoursesForSelection] = useState<any[]>([]);
  const [classesForSelection, setClassesForSelection] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<string | undefined>();
  const [loadingSource, setLoadingSource] = useState(false);

  const [initialDataLoading, setInitialDataLoading] = useState(true);

  // Main data fetching and processing function
  const fetchAndProcessLopHocPhans = useCallback(async () => {
      setLoading(true);
    try {
      const params: LopHocPhanListParams = {
        page: 1,
        limit: 10000,
        search: searchTerm || undefined,
        khoaHocId: selectedCourse || undefined,
        monHocId: selectedSubject || undefined,
      };

      const response = await lopHocPhanService.getLopHocPhanList(params);

      if (response.success && Array.isArray(response.data)) {
        // Grouping logic is now inside this function
        const grouped = groupData(response.data);
        setGroupedData(grouped);
        setLopHocPhans(response.data);
        setPagination(prev => ({ ...prev, totalItems: grouped.length, totalPages: Math.ceil(grouped.length / prev.pageSize), currentPage: 1 }));
      } else {
        setGroupedData([]);
        setLopHocPhans([]);
      }
    } catch (error) {
      // ... (error handling)
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCourse, selectedSubject, courses, subjects, pagination.pageSize]);

  // Initial data load
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [coursesRes, subjectsRes, teachersRes] = await Promise.all([
          courseService.getCourses({ page: 1, limit: 1000 }),
          listSubjects({ page: 1, limit: 1000 }),
          fetch((await import('../../config/apiConfig')).buildAPIURL('/users/teachers'), {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`,
              'Content-Type': 'application/json'
            }
          }).then(res => res.json())
        ]);
        setCourses(coursesRes.data || []);
        setSubjects(subjectsRes.data || []);
        setTeachers(teachersRes.success ? teachersRes.data : []);
      } catch (error) {
        message.error('Lỗi khi tải dữ liệu ban đầu');
      } finally {
        setInitialDataLoading(false);
      }
    };
    fetchInitialData();
  }, []);

  // Fetch and process data when filters change or initial data is loaded
  useEffect(() => {
    if (!initialDataLoading) {
      fetchAndProcessLopHocPhans();
    }
  }, [initialDataLoading, fetchAndProcessLopHocPhans]);

  const groupData = (data: LopHocPhanWithJoins[]) => {
    const grouped: GroupedLopHocPhan[] = [];
    // ... (logic của hàm groupDataByCoursesAndSubjects)

    data.forEach(lhp => {
      const courseId = lhp.KhoaHocID;
      const courseName = lhp.TenKhoaHoc;
      const subjectId = lhp.MaMonHoc;
      const subjectName = lhp.TenMonHoc;

      if (!courseId || !subjectId) {
        return;
      }

      let courseGroup = grouped.find(g => g.course.KhoaHocID === courseId);
      if (!courseGroup) {
        courseGroup = {
          course: {
            KhoaHocID: courseId,
            TenKhoaHoc: courseName || courseId,
            NganhHocID: '',
            IsActive: true
          },
          subjects: []
        };
        grouped.push(courseGroup);
      }

      let subjectGroup = courseGroup.subjects.find(s => s.subject.MonHocID === subjectId);
      if (!subjectGroup) {
        subjectGroup = {
          subject: {
            MonHocID: subjectId,
            TenMonHoc: subjectName || subjectId,
            SoTC: 0,
            HocKy: typeof lhp.HocKy === 'number' ? lhp.HocKy : parseInt(String(lhp.HocKy)) || 1,
            KhoaHocID: courseId
          },
          lopHocPhans: []
        };
        courseGroup.subjects.push(subjectGroup);
      }

      subjectGroup.lopHocPhans.push(lhp);
    });

    return grouped;
  };

  const handleEdit = (record: LopHocPhanWithJoins) => {
    setEditingLopHocPhan(record);
    form.setFieldsValue({
      MaLHP: record.MaLHP,
      TenLHP: record.TenLHP,
      MaMonHoc: record.MaMonHoc,
      GiangVienPhuTrachID: record.GiangVienPhuTrachID
    });
    setModalVisible(true);
  };

  const handleDelete = async (maLHP: string) => {
    try {
      const response = await lopHocPhanService.deleteLopHocPhan(maLHP);
      if (response.success) {
        message.success('Xóa lớp học phần thành công');
        fetchAndProcessLopHocPhans();
      }
    } catch (error: any) {
      console.error('Error deleting lop hoc phan:', error);
      if (error.response?.status === 401) {
        AuthService.logout();
        return;
      }
      message.error(error.response?.data?.message || 'Lỗi khi xóa lớp học phần');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // Convert GiangVienPhuTrachID to string if it exists, otherwise send undefined
      const requestData: LopHocPhanCreateRequest | LopHocPhanUpdateRequest = {
        MaLHP: values.MaLHP,
        TenLHP: values.TenLHP,
        MaMonHoc: values.MaMonHoc,
        GiangVienPhuTrachID: values.GiangVienPhuTrachID ? String(values.GiangVienPhuTrachID) : undefined
      };

      let response;
      if (editingLopHocPhan) {
        response = await lopHocPhanService.updateLopHocPhan(editingLopHocPhan.MaLHP, requestData);
      } else {
        response = await lopHocPhanService.createLopHocPhan(requestData as LopHocPhanCreateRequest);
      }

      if (response.success) {
        message.success(editingLopHocPhan ? 'Cập nhật lớp học phần thành công' : 'Tạo lớp học phần thành công');
        setModalVisible(false);
        setEditingLopHocPhan(null);
        form.resetFields();
        fetchAndProcessLopHocPhans();
      }
    } catch (error: any) {
      console.error('Error saving lop hoc phan:', error);
      if (error.response?.status === 401) {
        AuthService.logout();
        return;
      }
      message.error(error.response?.data?.message || 'Lỗi khi lưu lớp học phần');
    }
  };

  const handleSync = async () => {
    try {
      setLoading(true);
      const syncData: LopHocPhanSyncRequest = {
        KhoaHocID: selectedCourse || '',
        MonHocID: selectedSubject || ''
      };
      
      const response = await lopHocPhanService.syncLopHocPhan(syncData);
      if (response.success) {
        message.success(`Đồng bộ thành công: ${response.added || 0} thêm mới, ${response.updated || 0} cập nhật`);
        fetchAndProcessLopHocPhans();
      }
    } catch (error: any) {
      console.error('Error syncing lop hoc phans:', error);
      if (error.response?.status === 401) {
        AuthService.logout();
        return;
      }
      message.error(error.response?.data?.message || 'Lỗi khi đồng bộ lớp học phần');
    } finally {
      setLoading(false);
    }
  };

  // Sync All from selected course (following legacy pattern)
  const handleSyncAll = () => {
    setSyncAllModalVisible(true);
    syncAllForm.resetFields();
  };

  const handleSyncAllSubmit = async () => {
    try {
      const values = await syncAllForm.validateFields();
      setSyncAllLoading(true);
      
      const response = await lopHocPhanService.syncAllLopHocPhan(values.KhoaHocID);
      if (response.success) {
        message.success(`Đồng bộ ALL thành công: ${response.added || 0} thêm mới, ${response.updated || 0} cập nhật`);
        setSyncAllModalVisible(false);
        fetchAndProcessLopHocPhans();
      }
    } catch (error: any) {
      console.error('Error syncing all lop hoc phans:', error);
      if (error.response?.status === 401) {
        AuthService.logout();
        return;
      }
      message.error(error.response?.data?.message || 'Lỗi khi đồng bộ ALL lớp học phần');
    } finally {
      setSyncAllLoading(false);
    }
  };

  const showStudentModal = async (lhp: any) => {
    setCurrentLHP(lhp);
    setLoadingStudents(true);
    setStudentModalVisible(true);

    // Reset selections
    setSelectedCourse(undefined);
    setSelectedClass(undefined);
    setSourceStudents([]);
    setClassesForSelection([]);

    try {
      // 1. Get students already in the class section for the right panel
      const enrolledResult = await lopHocPhanService.getStudentsInClassSection(lhp.MaLHP);
      if (enrolledResult && enrolledResult.data) {
        setTargetKeys(enrolledResult.data.map((s: any) => s.MaHS));
      }

      // 2. Get all courses for the dropdown
      const courseResult = await courseService.getCourses({ page: 1, limit: 1000 });
      if (courseResult && courseResult.data) {
        setCoursesForSelection(courseResult.data);
      }
    } catch (error) {
      console.error("Error fetching initial data for student modal:", error);
      message.error("Không thể tải dữ liệu ban đầu.");
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleCourseSelectionChange = async (courseId: string) => {
    setSelectedCourse(courseId);
    setSelectedClass(undefined); // Reset class selection
    setSourceStudents([]); // Clear student list
    if (!courseId) {
      setClassesForSelection([]);
      return;
    }
    setLoadingSource(true);
    try {
      const classResult = await classService.listClasses({ khoaHocId: courseId, limit: 1000 });
      setClassesForSelection(classResult.data || []);
    } catch (error) {
      message.error("Không thể tải danh sách lớp học.");
    } finally {
      setLoadingSource(false);
    }
  };

  const handleClassSelectionChange = async (classId: string) => {
    setSelectedClass(classId);
    if (!classId) {
      setSourceStudents([]);
      return;
    }
    setLoadingSource(true);
    try {
      const studentResult = await sinhVienService.getByClass(classId, { limit: 2000 });
      setSourceStudents(
        studentResult.data.map((s: any) => ({
          key: s.MaHS,
          title: `${s.Hoten} (${s.MaHS})`,
          description: s.MaHS,
        }))
      );
    } catch (error) {
      message.error("Không thể tải danh sách sinh viên.");
    } finally {
      setLoadingSource(false);
    }
  };

  const handleStudentTransferChange = (
    newTargetKeys: React.Key[],
  ) => {
    setTargetKeys(newTargetKeys as string[]);
  };

  const handleStudentModalOk = async () => {
    if (!currentLHP) return;

    setLoadingStudents(true);
    try {
      await lopHocPhanService.updateStudentsInClassSection(currentLHP.MaLHP, targetKeys);
      message.success("Cập nhật danh sách sinh viên thành công!");
      setStudentModalVisible(false);
      fetchAndProcessLopHocPhans(); // Refresh to update student count
    } catch (error) {
      console.error("Error updating students:", error);
      message.error("Lỗi khi cập nhật danh sách sinh viên.");
    } finally {
      setLoadingStudents(false);
    }
  };

  // New function: Sync students from secondary database for selected class
  const handleSyncStudentsFromSecondaryDB = async () => {
    if (!currentLHP) {
      message.warning("Không có lớp học phần nào được chọn!");
      return;
    }

    if (!selectedClass) {
      message.warning("Vui lòng chọn lớp học trước khi đồng bộ!");
      return;
    }

    Modal.confirm({
      title: 'Xác nhận đồng bộ sinh viên',
      content: `Bạn có chắc chắn muốn đồng bộ sinh viên từ CSDL phụ cho lớp học phần "${currentLHP.TenLHP}"? Thao tác này sẽ tự động thêm sinh viên vào lớp học phần.`,
      okText: 'Đồng bộ',
      cancelText: 'Hủy',
      onOk: async () => {
        setSyncStudentsLoading(true);
        try {
          // Call API to sync students from secondary DB
          const response = await sinhVienService.syncStudentsByClass(selectedClass);
          
          if (response.success) {
            const count = response.data?.count || 0;
            const skipped = response.data?.skippedMissing || 0;
            message.success(`Đã đồng bộ ${count} sinh viên từ CSDL phụ!${skipped > 0 ? ` (Bỏ qua ${skipped} sinh viên chưa có trong hệ thống)` : ''}`);
            
            // Reload students list after sync
            await handleClassSelectionChange(selectedClass);
            
            // Reload enrolled students
            const enrolledResult = await lopHocPhanService.getStudentsInClassSection(currentLHP.MaLHP);
            if (enrolledResult && enrolledResult.data) {
              setTargetKeys(enrolledResult.data.map((s: any) => s.MaHS));
            }
          }
        } catch (error: any) {
          console.error('Error syncing students from secondary DB:', error);
          message.error(error.response?.data?.message || 'Lỗi khi đồng bộ sinh viên từ CSDL phụ');
        } finally {
          setSyncStudentsLoading(false);
        }
      }
    });
  };

  const renderLopHocPhanTable = (lopHocPhans: LopHocPhanWithJoins[]) => {
    const columns: ColumnsType<LopHocPhanWithJoins> = [
      {
        title: 'Mã LHP',
        dataIndex: 'MaLHP',
        key: 'MaLHP',
        width: 120,
        fixed: 'left',
        ellipsis: true
      },
      {
        title: 'Tên lớp học phần',
        dataIndex: 'TenLHP',
        key: 'TenLHP',
        width: 250,
        ellipsis: true
      },
      {
        title: 'Học kỳ',
        dataIndex: 'HocKy',
        key: 'HocKy',
        width: 80,
        align: 'center'
      },
      {
        title: 'Năm học',
        dataIndex: 'NamHoc',
        key: 'NamHoc',
        width: 100,
        align: 'center'
      },
      {
        title: 'Số SV',
        dataIndex: 'SoSinhVien',
        key: 'SoSinhVien',
        width: 80,
        align: 'center',
        render: (count: number) => (
          <Tag color={count > 0 ? 'blue' : 'default'}>
            {count || 0}
          </Tag>
        )
      },
      {
        title: 'Giảng viên phụ trách',
        dataIndex: 'TenGiangVien',
        key: 'TenGiangVien',
        width: 200,
        ellipsis: true,
        render: (tenGiangVien: string) => (
          tenGiangVien ? (
            <Tag color="green" icon={<UserOutlined />}>
              {tenGiangVien}
            </Tag>
          ) : (
            <Tag color="default">Chưa phân công</Tag>
          )
        )
      },
      {
        title: 'Hành động',
        key: 'actions',
        width: 150,
        render: (_, record) => (
          <Space size="small">
            <Tooltip title="Sửa">
              <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
            </Tooltip>
            <Tooltip title="Xóa">
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa lớp học phần này?"
              onConfirm={() => handleDelete(record.MaLHP)}
              okText="Xóa"
              cancelText="Hủy"
            >
                <Button danger icon={<DeleteOutlined />} size="small" />
              </Popconfirm>
            </Tooltip>
            <Tooltip title="Chọn sinh viên">
              <Button icon={<UsergroupAddOutlined />} size="small" onClick={() => showStudentModal(record)} />
            </Tooltip>
            <Tooltip title="Đồng bộ sinh viên từ CSDL phụ">
              <Button 
                type="primary"
                icon={<SyncOutlined />} 
                size="small" 
                loading={syncStudentsLoading && currentLHP?.MaLHP === record.MaLHP}
                onClick={async () => {
                  setCurrentLHP(record);
                  Modal.confirm({
                    title: 'Xác nhận đồng bộ',
                    content: `Bạn có chắc chắn muốn đồng bộ sinh viên từ CSDL phụ cho lớp học phần "${record.TenLHP}"?`,
                    okText: 'Đồng bộ',
                    cancelText: 'Hủy',
                    onOk: async () => {
                      try {
                        setSyncStudentsLoading(true);
                        // Use MaLHP which is LopHocPhanID in the database
                        const response = await sinhVienService.syncStudentsByClass(record.MaLHP);
                        const count = response.data?.count || 0;
                        const skipped = response.data?.skippedMissing || 0;
                        message.success(`Đã đồng bộ thành công ${count} sinh viên!${skipped > 0 ? ` (Bỏ qua ${skipped} sinh viên chưa có trong hệ thống)` : ''}`);
                        // Reload data
                        fetchAndProcessLopHocPhans();
                      } catch (error: any) {
                        console.error('Error syncing students:', error);
                        message.error(error.response?.data?.error || error.response?.data?.message || 'Lỗi khi đồng bộ sinh viên');
                      } finally {
                        setSyncStudentsLoading(false);
                      }
                    }
                  });
                }}
              />
            </Tooltip>
          </Space>
        ),
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={lopHocPhans}
        rowKey="MaLHP"
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
        style={{ overflowX: 'auto' }}
      />
    );
  };

  const renderGroupedContent = () => {
    if (groupedData.length === 0) {
      return <div style={{ textAlign: 'center', padding: '50px' }}>Không có dữ liệu</div>;
    }

    // Apply pagination to courseGroups using pagination.pageSize
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    const paginatedCourses = groupedData.slice(startIndex, endIndex);

    return (
      <div style={{ textAlign: 'left' }}>
        <Collapse>
        {paginatedCourses.map(courseGroup => (
          <Panel
            key={courseGroup.course.KhoaHocID}
            header={
              <div style={{ textAlign: 'left', width: '100%' }}>
                <Space>
                  <strong>{courseGroup.course.TenKhoaHoc}</strong>
                  <Tag color="blue">
                    {courseGroup.subjects.reduce((total, sg) => total + sg.lopHocPhans.length, 0)} lớp học phần
                  </Tag>
                </Space>
              </div>
            }
          >
            <Collapse>
              {courseGroup.subjects.map(subjectGroup => (
                <Panel
                  key={subjectGroup.subject.MonHocID}
                  header={
                    <div style={{ textAlign: 'left', width: '100%' }}>
                      <Space>
                        <span>{subjectGroup.subject.TenMonHoc}</span>
                        <Tag color="green">{subjectGroup.lopHocPhans.length} lớp</Tag>
                        <Tag color="orange">
                          {subjectGroup.lopHocPhans.reduce((total, lhp) => total + (lhp.SoSinhVien || 0), 0)} sinh viên
                        </Tag>
                      </Space>
                    </div>
                  }
                >
                  {renderLopHocPhanTable(subjectGroup.lopHocPhans)}
                </Panel>
              ))}
            </Collapse>
          </Panel>
        ))}
      </Collapse>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px', height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Title level={2}>Quản lý Lớp học phần</Title>
          
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input.Search
                placeholder="Tìm kiếm lớp học phần..."
                allowClear
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onSearch={fetchAndProcessLopHocPhans}
                enterButton={<SearchOutlined />}
              />
            </Col>
            <Col xs={12} sm={6} md={4} lg={4}>
              <Select
                placeholder="Chọn khóa học"
                allowClear
                style={{ width: '100%' }}
                value={selectedCourse}
                onChange={handleCourseSelectionChange}
              >
                {courses.map(course => (
                  <Option key={course.KhoaHocID} value={course.KhoaHocID}>
                    {course.TenKhoaHoc}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} sm={6} md={4} lg={4}>
              <Select
                placeholder="Chọn môn học"
                allowClear
                style={{ width: '100%' }}
                value={selectedSubject}
                onChange={setSelectedSubject}
              >
                {subjects.map(subject => (
                  <Option key={subject.MonHocID} value={subject.MonHocID}>
                    {subject.TenMonHoc}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={24} md={12} lg={10}>
              <Space wrap style={{ width: '100%', justifyContent: 'flex-start' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditingLopHocPhan(null);
                    form.resetFields();
                    setModalVisible(true);
                  }}
                >
                  Thêm lớp học phần
                </Button>
                <Button
                  icon={<SyncOutlined />}
                  onClick={handleSync}
                  loading={loading}
                >
                  Đồng bộ
                </Button>
                <Button
                  icon={<SyncOutlined />}
                  onClick={handleSyncAll}
                  loading={syncAllLoading}
                >
                  Đồng bộ ALL từ khóa học
                </Button>
                <Button icon={<DownloadOutlined />}>
                  Xuất Excel
                </Button>
                <Button icon={<UploadOutlined />}>
                  Nhập Excel
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Debug Information */}
        <Alert
          message={`Debug Info: ${lopHocPhans.length} lớp học phần, ${groupedData.length} khóa học`}
          type="info"
          style={{ marginBottom: 16 }}
          showIcon
        />

        <div style={{ minHeight: '400px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
              <SyncOutlined spin style={{ fontSize: '24px' }} />
              <div style={{ marginTop: '16px' }}>Đang tải dữ liệu...</div>
            </div>
          ) : (
            renderGroupedContent()
          )}
        </div>
        
        <div style={{ marginTop: 16, textAlign: 'right', borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
          <Space wrap>
            <span>Hiển thị:</span>
            <Select
              value={pagination.pageSize}
              onChange={(value) => setPagination((prev: PaginationInfo) => ({ ...prev, pageSize: value, currentPage: 1 }))}
              style={{ width: 80 }}
            >
              <Option value={10}>10</Option>
              <Option value={25}>25</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
            </Select>
            <span>
              Trang {pagination.currentPage} / {pagination.totalPages} 
              (Tổng: {pagination.totalItems} bản ghi)
            </span>
            <Button
              disabled={pagination.currentPage === 1}
              onClick={() => {
                setPagination((prev: PaginationInfo) => ({ ...prev, currentPage: prev.currentPage - 1 }));
              }}
            >
              Trước
            </Button>
            <Button
              disabled={pagination.currentPage === pagination.totalPages}
              onClick={() => {
                setPagination((prev: PaginationInfo) => ({ ...prev, currentPage: prev.currentPage + 1 }));
              }}
            >
              Sau
            </Button>
          </Space>
        </div>
      </Card>

      <Modal
        title={editingLopHocPhan ? 'Chỉnh sửa lớp học phần' : 'Thêm lớp học phần mới'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          setEditingLopHocPhan(null);
          form.resetFields();
        }}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            MaLHP: '',
            TenLHP: '',
            MaMonHoc: '',
            GiangVienPhuTrachID: undefined
          }}
        >
          <Form.Item
            label="Mã lớp học phần"
            name="MaLHP"
            rules={[{ required: true, message: 'Vui lòng nhập mã lớp học phần!' }]}
          >
            <Input placeholder="Nhập mã lớp học phần" />
          </Form.Item>

          <Form.Item
            label="Tên lớp học phần"
            name="TenLHP"
            rules={[{ required: true, message: 'Vui lòng nhập tên lớp học phần!' }]}
          >
            <Input placeholder="Nhập tên lớp học phần" />
          </Form.Item>

          <Form.Item
            label="Môn học"
            name="MaMonHoc"
            rules={[{ required: true, message: 'Vui lòng chọn môn học!' }]}
          >
            <Select placeholder="Chọn môn học" showSearch optionFilterProp="children">
              {subjects.map(subject => (
                <Option key={subject.MonHocID} value={subject.MonHocID}>
                  {subject.TenMonHoc}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Giảng viên phụ trách"
            name="GiangVienPhuTrachID"
          >
            <Select placeholder="Chọn giảng viên phụ trách" showSearch optionFilterProp="children" allowClear>
              {teachers.map(teacher => (
                <Option key={teacher.UserId} value={teacher.UserId}>
                  {teacher.FullName} ({teacher.Username})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Sync All Modal - following legacy pattern */}
      <Modal
        title="Đồng bộ ALL lớp học phần từ khóa học"
        open={syncAllModalVisible}
        onOk={handleSyncAllSubmit}
        onCancel={() => setSyncAllModalVisible(false)}
        confirmLoading={syncAllLoading}
        width={500}
      >
        <Alert
          message="Đồng bộ ALL lớp học phần"
          description="Chức năng này sẽ đồng bộ toàn bộ lớp học phần của tất cả môn học trong khóa học được chọn từ CSDL phụ. Các lớp học phần mới sẽ được thêm và cập nhật."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Form form={syncAllForm} layout="vertical">
          <Form.Item
            label="Chọn khóa học"
            name="KhoaHocID"
            rules={[{ required: true, message: 'Vui lòng chọn khóa học' }]}
          >
            <Select placeholder="Chọn khóa học để đồng bộ ALL">
              {courses.map(course => (
                <Option key={course.KhoaHocID} value={course.KhoaHocID}>
                  {course.TenKhoaHoc}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Chọn sinh viên cho lớp học phần: ${currentLHP?.TenLHP || ''}`}
        open={studentModalVisible}
        onOk={handleStudentModalOk}
        onCancel={() => setStudentModalVisible(false)}
        width={800}
        confirmLoading={loadingStudents}
      >
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Select
              placeholder="1. Chọn khóa học"
              style={{ width: '100%' }}
              value={selectedCourse}
              onChange={handleCourseSelectionChange}
              loading={loadingStudents}
              allowClear
            >
              {coursesForSelection.map(course => (
                <Select.Option key={course.KhoaHocID} value={course.KhoaHocID}>
                  {course.TenKhoaHoc}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={12}>
            <Select
              placeholder="2. Chọn lớp học"
              style={{ width: '100%' }}
              value={selectedClass}
              onChange={handleClassSelectionChange}
              disabled={!selectedCourse || classesForSelection.length === 0}
              loading={loadingSource}
              allowClear
            >
              {classesForSelection.map(cls => (
                <Select.Option key={cls.LopHocID} value={cls.LopHocID}>
                  {cls.TenLop}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* Sync button - only show when class is selected */}
        {selectedClass && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Alert
                message="Đồng bộ sinh viên từ CSDL phụ"
                description="Nhấn nút bên dưới để đồng bộ sinh viên từ cơ sở dữ liệu phụ cho lớp học đã chọn. Sinh viên mới sẽ được thêm vào danh sách bên trái."
                type="info"
                showIcon
                style={{ marginBottom: 8 }}
              />
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={handleSyncStudentsFromSecondaryDB}
                loading={syncStudentsLoading}
                block
              >
                Đồng bộ sinh viên từ CSDL phụ
              </Button>
            </Col>
          </Row>
        )}

        <Transfer
          dataSource={sourceStudents}
          showSearch
          targetKeys={targetKeys}
          onChange={(keys) => handleStudentTransferChange(keys)}
          render={item => item.title}
          listStyle={{
            width: 350,
            height: 400,
          }}
          titles={['Sinh viên chưa có trong lớp', 'Sinh viên đã có trong lớp']}
          filterOption={(inputValue, option) =>
            !!option.title && option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
          }
        />
      </Modal>
    </div>
  );
};

export default LopHocPhanManagementPage; 